/**
 * Utility functions for handling timetable time parsing and sorting
 */

/**
 * Parses a time string and converts it to 24-hour format for comparison
 * Handles various formats like:
 * - "08:30 AM-10:00 AM" 
 * - "10:15 AM-11:45 AM"
 * - "12:00 PM-01:30 PM" 
 * - "04:01 PM 06:00 PM" (inconsistent format without dash)
 */
export function parseStartTime(timeString: string): number {
  if (!timeString) return 0;
  
  // Handle different separator formats (- or space)
  let startTime: string;
  if (timeString.includes('-')) {
    startTime = timeString.split('-')[0].trim();
  } else if (timeString.includes(' ')) {
    // Handle format like "04:01 PM 06:00 PM"
    const parts = timeString.split(' ');
    if (parts.length >= 2) {
      startTime = `${parts[0]} ${parts[1]}`;
    } else {
      startTime = parts[0];
    }
  } else {
    startTime = timeString;
  }
  
  return convertToMinutes(startTime);
}

/**
 * Converts time string like "08:30 AM" or "02:15 PM" to minutes since midnight
 * This allows for easy numeric comparison for sorting
 */
function convertToMinutes(timeStr: string): number {
  try {
    // Remove extra spaces and normalize
    const normalizedTime = timeStr.trim().toUpperCase();
    
    // Extract time part and AM/PM part
    const timeMatch = normalizedTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/);
    
    if (!timeMatch) {
      console.warn(`Could not parse time: ${timeStr}`);
      return 0;
    }
    
    let hours = parseInt(timeMatch[1]);
    const minutes = parseInt(timeMatch[2]);
    const period = timeMatch[3];
    
    // Handle 12-hour to 24-hour conversion
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    // If no AM/PM specified, assume 24-hour format or make reasonable assumption
    if (!period) {
      // If hour is <= 7, assume it's PM (afternoon classes)
      // If hour is >= 8, assume it's AM (morning classes)
      if (hours <= 7 && hours !== 0) {
        hours += 12;
      }
    }
    
    return hours * 60 + minutes;
  } catch (error) {
    console.warn(`Error parsing time "${timeStr}":`, error);
    return 0;
  }
}

/**
 * Sorts an array of timetable items by their start time
 * Each item should have a 'time' property with the time string
 */
export function sortByTime<T extends { time: string }>(items: T[]): T[] {
  return items.sort((a, b) => {
    const timeA = parseStartTime(a.time);
    const timeB = parseStartTime(b.time);
    return timeA - timeB;
  });
}

/**
 * Compares two time strings and returns -1, 0, or 1 for sorting
 * Can be used as a comparator function in Array.sort()
 */
export function compareTimeStrings(timeA: string, timeB: string): number {
  const minutesA = parseStartTime(timeA);
  const minutesB = parseStartTime(timeB);
  return minutesA - minutesB;
}

/**
 * Formats minutes since midnight back to readable time
 * Useful for debugging or displaying sorted times
 */
export function minutesToTimeString(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
}

/**
 * Determines the status of a class based on current time
 * Returns 'upcoming', 'running', or 'completed'
 */
export type ClassStatus = 'upcoming' | 'running' | 'completed';

export function getClassStatus(timeString: string): ClassStatus {
  if (!timeString) return 'upcoming';
  
  try {
    // Parse the time range (e.g., "08:30 AM-10:00 AM")
    const parts = timeString.split('-');
    if (parts.length !== 2) return 'upcoming';
    
    const startTime = parts[0].trim();
    const endTime = parts[1].trim();
    
    // Convert to minutes since midnight
    const startMinutes = parseStartTime(startTime);
    const endMinutes = parseStartTime(endTime);
    
    // Get current time in minutes since midnight
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    
    // Determine status
    if (currentMinutes < startMinutes) {
      return 'upcoming';
    } else if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
      return 'running';
    } else {
      return 'completed';
    }
  } catch (error) {
    console.warn(`Error determining class status for "${timeString}":`, error);
    return 'upcoming';
  }
}

/**
 * Gets the appropriate badge color and text for a class status
 */
export function getStatusBadge(status: ClassStatus): { color: string; text: string; bgColor: string } {
  switch (status) {
    case 'running':
      return {
        color: 'text-green-700 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        text: 'Running'
      };
    case 'completed':
      return {
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-800/50',
        text: 'Completed'
      };
    case 'upcoming':
    default:
      return {
        color: 'text-blue-700 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        text: 'Upcoming'
      };
  }
}